import styled from "@emotion/styled";
import { Alert, TablePagination } from "@mui/material";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { C_OUTLINE_VARIANT, C_SURFACE } from "~/common/components/icon/Colors";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { useComponentSize } from "~/common/hooks/useComponentSize";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import type { TabularViewData } from "~/Search/models";
import {
  selectHasTubularResults,
  selectTabularViewData,
  selectVisibleTabularViewColumns,
} from "~/Search/redux/searchSelectors";
import { calculateTotalTableWidth, makeTechValueColumn, tsmakeNewDesignProductColumn } from "./TanStackColumnDefs";
import { useSearchPagination } from "../../useSearchPagination";
import { ELEVATION_5 } from "~/common/components/icon/Elevation";

interface Props {
  maxColumnAmount?: number;
}

export const NewDesignReduxProductTabularView = ({ maxColumnAmount }: Props) => {
  const { ref, width } = useComponentSize<HTMLDivElement>();
  const isMinTablet = useIsMinTabletSize();
  const isDesktop = useIsMinDesktopSize();
  const { t } = useTranslation();

  const fullConfig = useSelector(selectVisibleTabularViewColumns);
  const results = useSelector(selectTabularViewData);

  const config = fullConfig?.filter((col) => col.visible).slice(0, maxColumnAmount) ?? [];

  const { pageAmount, currentPage, onPageChanged, totalResults } = useSearchPagination();
  const rowsPerPage = 20;
  const start = currentPage * rowsPerPage + 1;
  const end = Math.min((currentPage + 1) * rowsPerPage, totalResults);

  // Remember to add unitMode from useUnit to dependency if memoized
  const tanstackColumns: ColumnDef<TabularViewData>[] = [
    tsmakeNewDesignProductColumn(t("page.search.tabular.products.title", { start, end, total: totalResults })),
    ...config.map((col) => makeTechValueColumn(col)),
  ];

  const totalTableWidth = calculateTotalTableWidth(tanstackColumns);
  const isTableLargerThanDesktop = totalTableWidth > width;

  const hasTubularResults = useSelector(selectHasTubularResults);

  let state = undefined;
  let fullWidth = true;
  if (!isMinTablet) {
    state = { columnPinning: { left: ["name"] } };
    // fullWidth = false;
  } else if (!isDesktop || isTableLargerThanDesktop) {
    state = { columnPinning: { left: ["name"] } };
    // fullWidth = false;
  } else {
    const availableWidth = width - tanstackColumns[0].size!;
    const oneCol = (availableWidth - tanstackColumns.length) / Math.max(tanstackColumns.length - 1, 1);

    tanstackColumns.slice(1, tanstackColumns.length).forEach((c) => (c.size = oneCol));
    fullWidth = false;
  }

  const table = (
    <NewSRPTable
      data={results}
      scrollbarOnTop={true}
      columns={tanstackColumns}
      fullwidth={fullWidth}
      variant={""}
      state={state}
      stickyHeader={true}
    />
  );

  return (
    <div ref={ref}>
      {!hasTubularResults && (
        <Alert severity={"info"} className={"scha__srp_pdp_tubular_no-results mb-2"}>
          {t("srp.plp.tubularView.noResults")}
        </Alert>
      )}
      {width > 0 && table}
      {pageAmount > 0 && (
        <StyledTablePagination
          component="div"
          count={totalResults}
          page={currentPage}
          onPageChange={(_, page) => onPageChanged(page)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
        />
      )}
    </div>
  );
};

const StyledTablePagination = styled(TablePagination)<{ component: React.ElementType }>({
  background: C_SURFACE,
  border: `1px solid ${C_OUTLINE_VARIANT}`,
  borderTop: `none`,
});

export const NewSRPTable = styled(TanStackTable)({
  border: `1px solid ${C_OUTLINE_VARIANT}`,
  borderTop: `none !important`,
  borderBottom: `none !important`,

  table: {
    borderCollapse: "separate",
  },
  thead: {
    background: C_SURFACE,
  },
  td: {
    padding: "8px !important",
    border: `1px solid ${C_OUTLINE_VARIANT}`,
    borderLeft: `none !important`,
    borderTop: `none !important`,
    ["&:last-child"]: {
      borderRight: `none !important`,
    },
  },
  th: {
    paddingTop: `${8 + 24}px`,
    verticalAlign: "middle",
    border: `1px solid ${C_OUTLINE_VARIANT} !important`,
    // ["&:not(:first-child)"]: {
    borderLeft: `none !important`,
    // },

    ["&:last-child"]: {
      borderRight: `none !important`,
    },
  },

  [".is-scrolled .pinned-left-last"]: {
    boxShadow: ELEVATION_5,
  },
});
